"use client"

import { useState, useEffect, useMemo } from "react"
import {
  getMembresiasBySucursal,
  createMembresia,
  updateMembresia,
  getIngresosMembresiasBySucursal,
} from "../../services/membresiasService"
import { getClienteByPlaca } from "../../services/clientesService"
import "./Membresias.css"
import { X, Search, Plus, TrendingUp, ChevronRight, FileText, FileSpreadsheet, Edit2, Users, Shield, Phone, User, LogIn, LogOut } from "lucide-react"

const MembresiaCreateModal = ({ onClose, onSave }) => {
  const [step, setStep] = useState(1)
  const [placa, setPlaca] = useState("")
  const [foundClient, setFoundClient] = useState(null)
  const [searchError, setSearchError] = useState("")
  const [formError, setFormError] = useState("")
  const [formData, setFormData] = useState({ fechaInicio: "", fechaFin: "", monto: "", activa: true })

  const handlePlacaSearch = async () => {
    if (!placa) {
      setSearchError("Por favor, ingrese una placa.")
      return
    }
    setSearchError("")
    try {
      const clientData = await getClienteByPlaca(placa)
      setFoundClient(clientData)
      setStep(2)
    } catch (err) {
      setSearchError("No se encontró ningún cliente con esa placa.")
      setFoundClient(null)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError("")
    const sucursalId = localStorage.getItem("idsucursal")
    if (!sucursalId) {
      setFormError("No se ha seleccionado una sucursal.")
      return
    }
    const payload = {
      cliente: { id: foundClient.id },
      sucursal: { id: Number.parseInt(sucursalId, 10) },
      ...formData,
      monto: Number.parseFloat(formData.monto),
      idTipoTarifa: 4,
    }
    try {
      await createMembresia(payload)
      onSave()
      onClose()
    } catch (err) {
      setFormError(err.message || "Ocurrió un error al crear la membresía.")
    }
  }

  return (
    <div className="mem-modal-overlay" onClick={onClose}>
      <div className="mem-modal-container mem-create-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mem-modal-header">
          <h3>Crear Nueva Membresía <span className="mem-modal-step-indicator">(Paso {step} de 2)</span></h3>
          <button onClick={onClose} className="mem-modal-close-btn">
            <X size={24} />
          </button>
        </div>
        <div className="mem-modal-content">
          {step === 1 && (
            <div className="mem-step-container">
              <h4 className="mem-modal-subtitle">Buscar Cliente por Placa</h4>
              <div className="mem-search-client-form">
                <input
                  type="text"
                  placeholder="Ingrese la placa del vehículo (AAA0000)"
                  value={placa}
                  onChange={(e) => setPlaca(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                  className="mem-placa-search-input"
                />
                <button onClick={handlePlacaSearch} className="mem-btn mem-btn-primary">
                  <Search size={16} />
                  Buscar
                </button>
              </div>
              {searchError && <p className="mem-form-error">{searchError}</p>}
            </div>
          )}
          {step === 2 && foundClient && (
            <form onSubmit={handleSubmit}>
              <h4 className="mem-modal-subtitle">Completar Datos de la Membresía</h4>
              <div className="mem-modal-client-info">
                  <div className="mem-info-item">
                    <User size={16} className="mem-info-icon" />
                    <span><strong>Cliente:</strong> {foundClient.nombre}</span>
                  </div>
                  <div className="mem-info-item">
                    <Shield size={16} className="mem-info-icon" />
                    <span><strong>Placa:</strong> {foundClient.placa}</span>
                  </div>
              </div>
              <div className="mem-form-grid">
                <div className="mem-form-group">
                  <label>Fecha Inicio</label>
                  <input type="date" name="fechaInicio" value={formData.fechaInicio} onChange={handleChange} required />
                </div>
                <div className="mem-form-group">
                  <label>Fecha Fin</label>
                  <input type="date" name="fechaFin" value={formData.fechaFin} onChange={handleChange} required />
                </div>
                <div className="mem-form-group">
                  <label>Monto</label>
                  <input
                    type="number"
                    step="0.01"
                    name="monto"
                    value={formData.monto}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mem-form-group-checkbox">
                  <label htmlFor="activa-create-checkbox">Membresía Activa</label>
                  <label className="mem-switch">
                    <input
                      id="activa-create-checkbox"
                      type="checkbox"
                      name="activa"
                      checked={formData.activa}
                      onChange={handleChange}
                    />
                    <span className="mem-slider mem-round"></span>
                  </label>
                </div>
              </div>
              {formError && <p className="mem-form-error">{formError}</p>}
              <div className="mem-form-actions">
                <button
                  type="button"
                  className="mem-btn mem-btn-secondary"
                  onClick={() => {
                    setFoundClient(null)
                    setStep(1)
                  }}
                >
                  Volver
                </button>
                <button type="submit" className="mem-btn mem-btn-primary">
                  <Plus size={16} />
                  Crear Membresía
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

const MembresiaEditModal = ({ membresia, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    clienteNombre: "",
    clienteTelefono: "",
    clientePlaca: "",
    clienteId: "",
    sucursalId: "",
    fechaInicio: "",
    fechaFin: "",
    monto: "",
    idTipoTarifa: "",
    activa: false,
  })
  const [error, setError] = useState("")

  useEffect(() => {
    if (membresia) {
      setFormData({
        clienteNombre: membresia.cliente?.nombre || "",
        clienteTelefono: membresia.cliente?.telefono || "",
        clientePlaca: membresia.cliente?.placa || "",
        clienteId: membresia.cliente?.id || "",
        sucursalId: membresia.sucursal?.id || "",
        fechaInicio: membresia.fechaInicio?.split("T")[0] || "",
        fechaFin: membresia.fechaFin?.split("T")[0] || "",
        monto: membresia.monto || "",
        idTipoTarifa: membresia.sucursal?.preciotarifa || "4",
        activa: membresia.activa || false,
      })
    }
  }, [membresia])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    try {
      await updateMembresia(membresia.id, formData)
      onSave()
      onClose()
    } catch (err) {
      setError(err.message || "Ocurrió un error al guardar.")
    }
  }

  return (
    <div className="mem-modal-overlay" onClick={onClose}>
      <div className="mem-modal-container mem-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mem-modal-header">
          <h3>Editar Membresía</h3>
          <button onClick={onClose} className="mem-modal-close-btn">
            <X size={24} />
          </button>
        </div>
        <form className="mem-modal-content" onSubmit={handleSubmit}>
          <input type="hidden" name="clienteId" value={formData.clienteId} />
          <input type="hidden" name="sucursalId" value={formData.sucursalId} />
          <input type="hidden" name="idTipoTarifa" value={formData.idTipoTarifa} />

          <div className="mem-form-section-title">Datos del Cliente</div>
          <div className="mem-modal-client-info">
            <div className="mem-info-item">
              <User size={16} className="mem-info-icon" />
              <span><strong>Cliente:</strong> {formData.clienteNombre}</span>
            </div>
            <div className="mem-info-item">
              <Shield size={16} className="mem-info-icon" />
              <span><strong>Placa:</strong> {formData.clientePlaca}</span>
            </div>
            <div className="mem-info-item">
              <Phone size={16} className="mem-info-icon" />
              <span><strong>Teléfono:</strong> {formData.clienteTelefono}</span>
            </div>
          </div>
          
          <div className="mem-form-section-title">Datos de la Membresía</div>
          <div className="mem-form-grid">
            <div className="mem-form-group">
              <label>Fecha Inicio</label>
              <input type="date" name="fechaInicio" value={formData.fechaInicio} onChange={handleChange} />
            </div>
            <div className="mem-form-group">
              <label>Fecha Fin</label>
              <input type="date" name="fechaFin" value={formData.fechaFin} onChange={handleChange} />
            </div>
            <div className="mem-form-group">
              <label>Monto</label>
              <input type="number" step="0.01" name="monto" value={formData.monto} onChange={handleChange} />
            </div>
            <div className="mem-form-group-checkbox">
              <label htmlFor="activa-edit-checkbox">Membresía Activa</label>
              <label className="mem-switch">
                <input
                  id="activa-edit-checkbox"
                  type="checkbox"
                  name="activa"
                  checked={formData.activa}
                  onChange={handleChange}
                />
                <span className="mem-slider mem-round"></span>
              </label>
            </div>
          </div>

          {error && <p className="mem-form-error">{error}</p>}
          <div className="mem-form-actions">
            <button type="button" className="mem-btn mem-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="mem-btn mem-btn-primary">
              <Edit2 size={16} />
              Guardar Cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const SidePanel = ({ isOpen, onClose, title, children }) => {
  return (
    <>
      {isOpen && <div className="mem-membresias-side-panel-overlay" onClick={onClose} />}
      <div className={`mem-membresias-side-panel ${isOpen ? "mem-open" : ""}`}>
        <div className="mem-membresias-side-panel-header">
          <h3>{title}</h3>
          <button onClick={onClose} className="mem-membresias-side-panel-close">
            <X size={20} />
          </button>
        </div>
        <div className="mem-membresias-side-panel-content">{children}</div>
      </div>
    </>
  )
}

const downloadPDF = (data, clienteName, selectedMonth, selectedYear) => {
  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ]
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Historial de Visitas - ${clienteName}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #920606; padding-bottom: 15px; }
        .header h1 { color: #920606; margin: 0; font-size: 24px; }
        .header p { margin: 5px 0; color: #666; font-size: 14px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
        th { background-color: #920606; color: white; font-weight: bold; }
        tr:nth-child(even) { background-color: #f9f9f9; }
        .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 15px; }
        .summary { background: #f0f0f0; padding: 15px; border-radius: 8px; margin-bottom: 20px; text-align: center; }
        .summary h3 { color: #920606; margin: 0; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Historial de Visitas</h1>
        <p><strong>Cliente:</strong> ${clienteName}</p>
        <p><strong>Período:</strong> ${monthNames[selectedMonth - 1]} ${selectedYear}</p>
        <p><strong>Fecha de generación:</strong> ${new Date().toLocaleDateString("es-ES")}</p>
      </div>
      <div class="summary"><h3>Total de visitas: ${data.length}</h3></div>
      <table>
        <thead><tr><th>N°</th><th>Fecha de Entrada</th><th>Fecha de Salida</th></tr></thead>
        <tbody>
          ${data.map((ingreso, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${ingreso.fechaIngreso ? new Date(ingreso.fechaIngreso).toLocaleString("es-ES") : "N/A"}</td>
              <td>${ingreso.fechaSalida ? new Date(ingreso.fechaSalida).toLocaleString("es-ES") : "N/A"}</td>
            </tr>`).join("")}
        </tbody>
      </table>
      <div class="footer"><p>Documento generado automáticamente</p></div>
    </body>
    </html>`
  const printWindow = window.open("", "_blank")
  printWindow.document.write(htmlContent)
  printWindow.document.close()
  printWindow.onload = () => {
    printWindow.print()
    printWindow.close()
  }
}

const downloadExcel = (data, clienteName, selectedMonth, selectedYear) => {
  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ]
  const headers = ["N°", "Fecha de Entrada", "Fecha de Salida"]
  const csvContent = [
    `Historial de Visitas - ${clienteName}`,
    `Período: ${monthNames[selectedMonth - 1]} ${selectedYear}`,
    `Total de visitas: ${data.length}`,
    `Generado: ${new Date().toLocaleDateString("es-ES")}`,
    "",
    headers.join(","),
    ...data.map((ingreso, index) =>
      [
        index + 1,
        ingreso.fechaIngreso ? new Date(ingreso.fechaIngreso).toLocaleString("es-ES") : "N/A",
        ingreso.fechaSalida ? new Date(ingreso.fechaSalida).toLocaleString("es-ES") : "N/A",
      ].join(","),
    ),
  ].join("\n")
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const link = document.createElement("a")
  const url = URL.createObjectURL(blob)
  link.setAttribute("href", url)
  link.setAttribute("download", `historial_${clienteName.replace(/\s+/g, "_")}_${monthNames[selectedMonth - 1]}_${selectedYear}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const ClienteIngresoCard = ({ clienteAgrupado, selectedYear, selectedMonth }) => {
  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const { cliente, ingresos } = clienteAgrupado

  const ingresosDelPeriodo = useMemo(() => {
    return ingresos.filter((ingreso) => {
      const fechaIngreso = new Date(ingreso.fechaIngreso)
      return fechaIngreso.getUTCFullYear() === selectedYear && fechaIngreso.getUTCMonth() + 1 === selectedMonth
    })
  }, [ingresos, selectedYear, selectedMonth])

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A"
    return new Date(dateTimeString).toLocaleString("es-ES", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
    })
  }

  const handleDownloadPDF = (e) => {
    e.stopPropagation()
    downloadPDF(ingresosDelPeriodo, cliente.nombre, selectedMonth, selectedYear)
  }

  const handleDownloadExcel = (e) => {
    e.stopPropagation()
    downloadExcel(ingresosDelPeriodo, cliente.nombre, selectedMonth, selectedYear)
  }

  return (
    <>
      <div className="mem-membresias-ingreso-card-compact">
        <div className="mem-membresias-card-header-compact" onClick={() => setSidePanelOpen(true)}>
          <div className="mem-membresias-client-main-info">
            <h3 className="mem-membresias-client-name-compact">{cliente.nombre}</h3>
            <div className="mem-membresias-client-details-compact">
              <div className="mem-membresias-detail-item mem-membresias-placa-compact">
                  <Shield size={12} className="mem-membresias-detail-icon" />
                  <span>{cliente.placa}</span>
              </div>
              <div className="mem-membresias-detail-item mem-membresias-phone-compact">
                  <Phone size={12} className="mem-membresias-detail-icon" />
                  <span>{cliente.telefono}</span>
              </div>
            </div>
          </div>
          <div className="mem-membresias-card-visits-compact">
            <span className="mem-membresias-visits-count">{ingresosDelPeriodo.length}</span>
            <span className="mem-membresias-visits-label"> Visitas</span>
          </div>
          <div className="mem-membresias-download-actions-compact">
            <button onClick={handleDownloadPDF} className="mem-membresias-download-btn-compact mem-membresias-download-pdf" title="PDF">
              <FileText size={14} />
            </button>
            <button onClick={handleDownloadExcel} className="mem-membresias-download-btn-compact mem-membresias-download-excel" title="Excel">
              <FileSpreadsheet size={14} />
            </button>
          </div>
          <ChevronRight className="mem-membresias-chevron-compact" />
        </div>
      </div>

      <SidePanel isOpen={sidePanelOpen} onClose={() => setSidePanelOpen(false)} title={`Historial de ${cliente.nombre}`}>
        <div className="mem-membresias-client-info-panel">
          <div className="mem-membresias-client-detail">
            <span>Placa: {cliente.placa}</span>
            <span>Teléfono: {cliente.telefono}</span>
          </div>
          <div className="mem-membresias-download-buttons-group">
            <button onClick={handleDownloadPDF} className="mem-membresias-download-full-btn mem-membresias-download-pdf">
              <FileText size={16} /> PDF
            </button>
            <button onClick={handleDownloadExcel} className="mem-membresias-download-full-btn mem-membresias-download-excel">
              <FileSpreadsheet size={16} /> Excel
            </button>
          </div>
        </div>
        <div className="mem-membresias-side-panel-list">
          {ingresosDelPeriodo.length > 0 ? (
            ingresosDelPeriodo.map((ingreso) => (
              <div key={ingreso.id} className="mem-membresias-side-panel-item">
                <div className="mem-info-item">
                  <LogIn size={16} className="mem-info-icon mem-income-icon" />
                  <span><strong>Entrada:</strong> {formatDateTime(ingreso.fechaIngreso)}</span>
                </div>
                <div className="mem-info-item">
                  <LogOut size={16} className="mem-info-icon mem-income-icon" />
                  <span><strong>Salida:</strong> {formatDateTime(ingreso.fechaSalida)}</span>
                </div>
              </div>
            ))
          ) : (
            <p className="mem-membresias-no-membresias-periodo">No hay ingresos en este período.</p>
          )}
        </div>
      </SidePanel>
    </>
  )
}

const ClienteMembresiasCard = ({ clienteAgrupado, onEdit, selectedYear, selectedMonth }) => {
  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const { cliente, membresias } = clienteAgrupado

  const membresiasDelPeriodo = useMemo(() => {
    return membresias.filter((m) => {
      const fechaInicio = new Date(m.fechaInicio)
      return fechaInicio.getUTCFullYear() === selectedYear && fechaInicio.getUTCMonth() + 1 === selectedMonth
    })
  }, [membresias, selectedYear, selectedMonth])

  const totalActivasDelPeriodo = useMemo(() => {
    return membresiasDelPeriodo.filter((m) => m.activa).reduce((acc, m) => acc + m.monto, 0)
  }, [membresiasDelPeriodo])

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("es-ES", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: 'UTC' })
  }

  return (
    <>
      <div className="mem-membresias-grid-card-compact">
        <div className="mem-membresias-card-header-compact" onClick={() => setSidePanelOpen(true)}>
          <div className="mem-membresias-client-main-info">
            <h3 className="mem-membresias-client-name-compact">{cliente.nombre}</h3>
            <div className="mem-membresias-client-details-compact">
              <div className="mem-membresias-detail-item mem-membresias-placa-compact">
                  <Shield size={12} className="mem-membresias-detail-icon" />
                  <span>{cliente.placa}</span>
              </div>
              <div className="mem-membresias-detail-item mem-membresias-phone-compact">
                  <Phone size={12} className="mem-membresias-detail-icon" />
                  <span>{cliente.telefono}</span>
              </div>
            </div>
          </div>
          <div className="mem-membresias-card-amount-compact">
            <span className="mem-membresias-amount-value">${totalActivasDelPeriodo.toFixed(2)}</span>
          </div>
          <ChevronRight className="mem-membresias-chevron-compact" />
        </div>
      </div>

      <SidePanel isOpen={sidePanelOpen} onClose={() => setSidePanelOpen(false)} title={`Membresías de ${cliente.nombre}`}>
        <div className="mem-membresias-client-info-panel">
          <div className="mem-membresias-client-detail">
            <span>Placa: {cliente.placa}</span>
            <span>Teléfono: {cliente.telefono}</span>
          </div>
        </div>
        <div className="mem-membresias-side-panel-list">
          {membresiasDelPeriodo.length > 0 ? (
            membresiasDelPeriodo.map((membresia) => (
              <div key={membresia.id} className="mem-membresias-side-panel-item">
                <div className="mem-membresias-panel-period">
                  <span>
                    Período: {formatDate(membresia.fechaInicio)} - {formatDate(membresia.fechaFin)}
                  </span>
                  <div className="mem-membresias-status-action-group">
                    <span className={`mem-membresias-status-badge-small ${membresia.activa ? "mem-activa" : "mem-expirada"}`}>
                      {membresia.activa ? "Activa" : "Expirada"}
                    </span>
                    {membresia.activa && (
                      <button
                        className="mem-membresias-edit-btn-small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(membresia);
                        }}
                        title="Editar membresía"
                      >
                        <Edit2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="mem-membresias-no-membresias-periodo">No hay membresías en este período.</p>
          )}
        </div>
      </SidePanel>
    </>
  )
}

const Membresias = () => {
  const [allMembresias, setAllMembresias] = useState([])
  const [allIngresos, setAllIngresos] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingIngresos, setLoadingIngresos] = useState(true)
  const [error, setError] = useState(null)
  const [errorIngresos, setErrorIngresos] = useState(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingMembresia, setEditingMembresia] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("todos")
  const [activeView, setActiveView] = useState("membresias")
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)

  const fetchMembresias = async () => {
    const sucursalId = localStorage.getItem("idsucursal")
    if (!sucursalId) {
      setError("No se ha seleccionado una sucursal.")
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const data = await getMembresiasBySucursal(sucursalId)
      setAllMembresias(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchIngresos = async () => {
    const sucursalId = localStorage.getItem("idsucursal")
    if (!sucursalId) {
      setErrorIngresos("No se ha seleccionado una sucursal.")
      setLoadingIngresos(false)
      return
    }
    try {
      setLoadingIngresos(true)
      const data = await getIngresosMembresiasBySucursal(sucursalId)
      setAllIngresos(Array.isArray(data) ? data : [])
    } catch (err) {
      setErrorIngresos(err.message)
    } finally {
      setLoadingIngresos(false)
    }
  }

  useEffect(() => {
    fetchMembresias()
    fetchIngresos()
  }, [])

  const { filteredGroups, totalMes } = useMemo(() => {
    const membresiasDelPeriodo = allMembresias.filter((m) => {
      const fechaInicio = new Date(m.fechaInicio)
      return fechaInicio.getUTCFullYear() === selectedYear && fechaInicio.getUTCMonth() + 1 === selectedMonth
    })
    const totalMes = membresiasDelPeriodo.filter((m) => m.activa).reduce((sum, m) => sum + m.monto, 0)
    const groups = membresiasDelPeriodo.reduce((acc, membresia) => {
      const clientId = membresia.cliente?.id
      if (!clientId) return acc
      if (!acc[clientId]) {
        acc[clientId] = { cliente: membresia.cliente, membresias: [] }
      }
      acc[clientId].membresias.push(membresia)
      return acc
    }, {})
    return {
      filteredGroups: Object.values(groups).filter((group) => {
        const searchMatch = `${group.cliente.nombre} ${group.cliente.placa}`.toLowerCase().includes(searchTerm.toLowerCase())
        const statusMatch = statusFilter === "todos" || group.membresias.some(m => statusFilter === (m.activa ? "activas" : "expiradas"))
        return searchMatch && statusMatch
      }),
      totalMes,
    }
  }, [allMembresias, searchTerm, statusFilter, selectedYear, selectedMonth])

  const filteredIngresosGroups = useMemo(() => {
    const ingresosDelPeriodo = allIngresos.filter((ingreso) => {
      const fechaIngreso = new Date(ingreso.fechaIngreso)
      return fechaIngreso.getUTCFullYear() === selectedYear && fechaIngreso.getUTCMonth() + 1 === selectedMonth
    })
    const groups = ingresosDelPeriodo.reduce((acc, ingreso) => {
      const clientId = ingreso.membresia.cliente?.id
      if (!clientId) return acc
      if (!acc[clientId]) {
        acc[clientId] = { cliente: ingreso.membresia.cliente, ingresos: [] }
      }
      acc[clientId].ingresos.push(ingreso)
      return acc
    }, {})
    return Object.values(groups).filter((group) =>
      `${group.cliente.nombre} ${group.cliente.placa}`.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [allIngresos, searchTerm, selectedYear, selectedMonth])

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i)
  const months = [
    { value: 1, name: "Enero" }, { value: 2, name: "Febrero" }, { value: 3, name: "Marzo" }, { value: 4, name: "Abril" },
    { value: 5, name: "Mayo" }, { value: 6, name: "Junio" }, { value: 7, name: "Julio" }, { value: 8, name: "Agosto" },
    { value: 9, name: "Septiembre" }, { value: 10, name: "Octubre" }, { value: 11, name: "Noviembre" }, { value: 12, name: "Diciembre" },
  ]

  if (loading && loadingIngresos) return <div className="mem-membresias-message-container">Cargando datos...</div>

  return (
    <div className="mem-membresias-page-container">
      <div className="mem-membresias-page-header">
        <div className="mem-membresias-title-container">
            <h1 className="mem-membresias-title-main">Gestión de Membresías</h1>
        </div>
        <div className="mem-membresias-header-decoration"></div>
        <div className="mem-membresias-view-selector">
          <button className={`mem-membresias-view-btn ${activeView === "membresias" ? "mem-active" : ""}`} onClick={() => setActiveView("membresias")}>
            <Users size={18} /> Membresías
          </button>
          <button className={`mem-membresias-view-btn ${activeView === "ingresos" ? "mem-active" : ""}`} onClick={() => setActiveView("ingresos")}>
            <TrendingUp size={18} /> Ingresos por Mes
          </button>
        </div>
      </div>

      <div className="mem-membresias-filter-bar">
        <div className="mem-membresias-search-container">
          <Search className="mem-membresias-search-icon" size={20} />
          <input type="text" placeholder="Buscar por nombre o placa..." className="mem-membresias-search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="mem-membresias-date-filters">
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="mem-membresias-date-select">
            {months.map((m) => <option key={m.value} value={m.value}>{m.name}</option>)}
          </select>
          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="mem-membresias-date-select">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {activeView === "membresias" && (
          <div className="mem-membresias-status-filter-container">
            <button className={`mem-membresias-filter-btn ${statusFilter === "todos" ? "mem-active" : ""}`} onClick={() => setStatusFilter("todos")}>Todos</button>
            <button className={`mem-membresias-filter-btn ${statusFilter === "activas" ? "mem-active" : ""}`} onClick={() => setStatusFilter("activas")}>Activas</button>
            <button className={`mem-membresias-filter-btn ${statusFilter === "expiradas" ? "mem-active" : ""}`} onClick={() => setStatusFilter("expiradas")}>Expiradas</button>
          </div>
        )}
      </div>

      {activeView === "membresias" && (
        <div className="mem-membresias-dashboard-stats-centered">
          <div className="mem-membresias-stat-card-total">
            <div className="mem-membresias-stat-content-total">
                <span className="mem-membresias-stat-label-total">
                    Total Membresías {months.find((m) => m.value === selectedMonth)?.name} {selectedYear}
                </span>
                <span className="mem-membresias-stat-value-total">${totalMes.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {activeView === "membresias" ? (
        <>
          {error && <div className="mem-membresias-message-container mem-error">Error: {error}</div>}
          <div className="mem-membresias-grid-compact">
            {filteredGroups.map((group) => (
              <ClienteMembresiasCard key={group.cliente.id} clienteAgrupado={group} onEdit={(membresia) => {
                setEditingMembresia(membresia);
              }} selectedYear={selectedYear} selectedMonth={selectedMonth} />
            ))}
          </div>
          <button onClick={() => setIsCreateModalOpen(true)} className="mem-membresias-add-fab-btn" title="Crear Nueva Membresía">
            <Plus size={24} />
          </button>
        </>
      ) : (
        <>
          {errorIngresos && <div className="mem-membresias-message-container mem-error">Error: {errorIngresos}</div>}
          <div className="mem-membresias-grid-compact">
            {filteredIngresosGroups.length > 0 ? (
              filteredIngresosGroups.map((group) => (
                <ClienteIngresoCard key={group.cliente.id} clienteAgrupado={group} selectedYear={selectedYear} selectedMonth={selectedMonth} />
              ))
            ) : (
              <div className="mem-membresias-message-container">No se encontraron ingresos para los filtros seleccionados.</div>
            )}
          </div>
        </>
      )}

      {isCreateModalOpen && <MembresiaCreateModal onClose={() => setIsCreateModalOpen(false)} onSave={fetchMembresias} />}
      {editingMembresia && <MembresiaEditModal membresia={editingMembresia} onClose={() => setEditingMembresia(null)} onSave={fetchMembresias} />}
    </div>
  )
}

export default Membresias